'use client';
import { Info } from '@phosphor-icons/react';
import React, { useId } from 'react';
import IconWrapper from '../IconWrapper/IconWrapper';
import { LanguageType, LanguageDirection } from '@/lib/types';

type InputFieldAppearance = "default" | "reversed";
type TextInputFieldState = "normal" | "disabled" | "filled" | "invalid";

interface TextInputFieldProps {
	label: string
	size: number
	hint?: string
	language?: LanguageType
	disabled?: boolean
	invalid?: boolean
	value?: string
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
	placeholder?: string
	optional?: boolean
	icon?: React.ReactElement<{ color?: string, size?: number, className?: string }>
	// "reversed" mirrors Figma's Style=Reversed variant (dark backgrounds).
	appearance?: InputFieldAppearance
}

const LANGUAGE_TO_FLEX_PLACEMENT: Record<LanguageType, string> = {
	english: "start",
	arabic: "end"
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
}

const SIZE_TO_TEXT_STYLE: Record<number, string> = {
	20: 'text-20-to-18--alexandria',
	18: 'text-18-fixed--alexandria',
}

const STATE_TO_BORDER: Record<TextInputFieldState, string> = {
	normal: `
		border-stroke-soft-200
		focus-within:border-theme-yellow-primary-base
	`,
	filled: `
		border-theme-yellow-primary-base
	`,
	disabled: `
		border-stroke-disabled
	`,
	invalid: `
		border-state-error-base
	`
}


const STATE_TO_LABEL_TEXT: Record<TextInputFieldState, string> = {
	normal: `
		text-text-tertiary
		group-hover:text-text-quinary
	`,
	filled: `
		text-text-tertiary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-text-tertiary
	`,
}

const STATE_TO_TEXT: Record<TextInputFieldState, string> = {
	normal: `
		text-text-disabled
	`,
	filled: `
		text-text-primary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-text-primary
	`,
}
const STATE_TO_HINT_TEXT: Record<TextInputFieldState, string> = {
	normal: `
		text-text-quaternary
	`,
	filled: `
		text-text-quaternary
	`,
	disabled: `
		text-text-disabled
	`,
	invalid: `
		text-state-error-base
	`,
}
const STATE_TO_ICON_TEXT: Record<TextInputFieldState, string> = {
	normal: `
		text-graphics-quaternary
	`,
	filled: `
		text-graphics-quaternary
	`,
	disabled: `
		text-graphics-disabled
	`,
	invalid: `
		text-state-error-base
	`,
}

// Color rules for the optional prefix icon that sits next to the <input>.
// Distinct from STATE_TO_ICON_TEXT (which colors the hint-row Info icon).
const STATE_TO_INPUT_ICON_TEXT: Record<TextInputFieldState, string> = {
	normal: `
		text-graphics-tertiary
		group-hover:opacity-50
	`,
	filled: `
		text-graphics-primary
	`,
	disabled: `
		text-graphics-disabled
	`,
	invalid: `
		text-graphics-primary
	`,
}

// Figma "Style=Reversed" variant - for fields sitting on dark surfaces.
const REVERSED_STATE_TO_BORDER: Record<TextInputFieldState, string> = {
	normal: `
		border-stroke-soft-200
		focus-within:border-theme-yellow-primary-light
	`,
	filled: `
		border-theme-yellow-primary-light
	`,
	disabled: `
		border-stroke-disabled
	`,
	invalid: `
		border-state-error-light
	`
}

const REVERSED_STATE_TO_LABEL_TEXT: Record<TextInputFieldState, string> = {
	normal: `text-text-reversed`,
	filled: `text-text-reversed`,
	disabled: `text-text-disabled`,
	invalid: `text-text-reversed`,
}

const REVERSED_STATE_TO_TEXT: Record<TextInputFieldState, string> = {
	normal: `text-text-disabled`,
	filled: `text-text-reversed`,
	disabled: `text-text-disabled`,
	invalid: `text-text-reversed`,
}

const REVERSED_STATE_TO_HINT_TEXT: Record<TextInputFieldState, string> = {
	normal: `text-text-reversed`,
	filled: `text-text-reversed`,
	disabled: `text-text-disabled`,
	invalid: `text-state-error-light`,
}

const REVERSED_STATE_TO_ICON_TEXT: Record<TextInputFieldState, string> = {
	normal: `text-graphics-reversed`,
	filled: `text-graphics-reversed`,
	disabled: `text-graphics-disabled`,
	invalid: `text-state-error-light`,
}

const REVERSED_STATE_TO_INPUT_ICON_TEXT: Record<TextInputFieldState, string> = {
	normal: `
		text-graphics-reversed
		group-hover:opacity-50
	`,
	filled: `text-graphics-reversed`,
	disabled: `text-graphics-disabled`,
	invalid: `text-graphics-reversed`,
}

type InputFieldStyles = {
	border: Record<TextInputFieldState, string>
	label: Record<TextInputFieldState, string>
	text: Record<TextInputFieldState, string>
	hint: Record<TextInputFieldState, string>
	icon: Record<TextInputFieldState, string>
	inputIcon: Record<TextInputFieldState, string>
	asterisk: string
}

const APPEARANCE_TO_STYLES: Record<InputFieldAppearance, InputFieldStyles> = {
	default: {
		border: STATE_TO_BORDER,
		label: STATE_TO_LABEL_TEXT,
		text: STATE_TO_TEXT,
		hint: STATE_TO_HINT_TEXT,
		icon: STATE_TO_ICON_TEXT,
		inputIcon: STATE_TO_INPUT_ICON_TEXT,
		asterisk: 'text-theme-yellow-primary-base',
	},
	reversed: {
		border: REVERSED_STATE_TO_BORDER,
		label: REVERSED_STATE_TO_LABEL_TEXT,
		text: REVERSED_STATE_TO_TEXT,
		hint: REVERSED_STATE_TO_HINT_TEXT,
		icon: REVERSED_STATE_TO_ICON_TEXT,
		inputIcon: REVERSED_STATE_TO_INPUT_ICON_TEXT,
		asterisk: 'text-theme-yellow-primary-light',
	},
}

const getState = (disabled: boolean, filled: boolean, invalid: boolean): TextInputFieldState => {
	if (disabled) return "disabled";
	if (invalid) return "invalid";
	if (filled) return "filled";

	return "normal";
}


const TextInputField: React.FC<TextInputFieldProps> = ({
	label,
	size,
	hint,
	language = "english",
	disabled = false,
	invalid = false,
	placeholder,
	value,
	onChange,
	optional = false,
	icon,
	appearance = "default",
}) => {
	const inputId = useId();
	const hintId = useId();
	const styles = APPEARANCE_TO_STYLES[appearance];

	const isFilled = value?.trim() !== '';

	return (
		<div className='w-full flex flex-row lg:flex-grow lg:min-w-[18rem]'>
			<div className='
				w-full
				flex flex-col gap-[0.75rem]
				group
				'>
				<div className={`
					flex flex-col gap-[1rem]
					transition-[border, colors] duration-500 ease-in-out
					border-b-main
					${styles.border[getState(disabled, isFilled, invalid)]}
					${`items-${LANGUAGE_TO_FLEX_PLACEMENT[language]}`}
				`}>
					<div
						style={{ direction: `${LANGUAGE_TO_DIRECTION[language]}` }}
						className={`
							flex flex-row gap-[0.125rem]
							items-center
							${`justify-${LANGUAGE_TO_FLEX_PLACEMENT[language]}`}
						`}>
						<label
							htmlFor={inputId}
							className={`
							text-16-fixed--alexandria
							transition-colors duration-300 ease-in-out
							${styles.label[getState(disabled, isFilled, invalid)]}
						`}
						>
							{label}
						</label>
						{!optional && <p aria-hidden="true" className={styles.asterisk}>*</p>}
					</div>
					<div
						style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
						className="flex flex-row gap-[0.25rem] w-full"
					>
						{icon &&
							<div className="p-[0.25rem]">
								<IconWrapper
									size={20}
									className={`
										transition-[color,opacity] duration-300 ease-in-out
										${styles.inputIcon[getState(disabled, isFilled, invalid)]}
									`}
								>
									{icon}
								</IconWrapper>
							</div>
						}
						<input
							suppressHydrationWarning
							id={inputId}
							disabled={disabled}
							required={!optional}
							aria-invalid={invalid || undefined}
							aria-describedby={hint ? hintId : undefined}
							style={{
								direction: `${LANGUAGE_TO_DIRECTION[language]}`
							}}
							type="text"
							placeholder={placeholder || ''}
							onChange={onChange}
							value={value}

							className={`
								pb-[1rem]
								w-full

								focus:outline-none focus:ring-0
								${SIZE_TO_TEXT_STYLE[size]}
								${styles.text[getState(disabled, isFilled, invalid)]}
							`}
						/>
					</div>
				</div>
				<div
					style={{
						direction: `${LANGUAGE_TO_DIRECTION[language]}`,
						paddingInlineEnd: '2rem',
						// visibility: hint ? 'visible' : 'hidden',
						height: '1rem'
					}}
					className={`
						flex 
						flex-row
						gap-[0.25rem] 

						items-center 
						justify-start
						transition-opacity duration-300 ease-in-out
						${hint ? 'opacity-100' : 'opacity-0'}
					`}>
					{hint && <IconWrapper className={`${styles.icon[getState(disabled, isFilled, invalid)]} self-center`} size={16}>
						<Info />
					</IconWrapper>}
					<p
						id={hintId}
						role={invalid ? 'alert' : undefined}
						className={`
						text-12-fixed--alexandria
						${styles.hint[getState(disabled, isFilled, invalid)]}
					`}>
						{hint || " "}
					</p>
				</div>
			</div >
		</div >
	);
};

export default TextInputField;
